// InvoiceDraftFilterForm.tsx
import moment from 'moment';
import React, { useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { KTSVG } from '../../..//_metronic/helpers';
import DateRangeComp from '../../../components/dateComponent/DateRangeComp';
import SearchSelect from '../../../components/textFields/SearchSelect';
import TextField from '../../../components/textFields/TextField';
import { useGetAllUsersManagerAdminQuery, useGetAllRecordSourceLookupQuery } from '../../../services/GeneralApi';
import labelKey from '../../localization/label.json';

interface InvoiceDraftFilterFormProps {
    onApplyFilters: (filters: any) => void;
    isApplyingFilters?: boolean;
    initialFilters?: any;
}

const statusOptions = [
    { value: "0", label: "All Except Attach" },
    { value: "1", label: "Ready To Use" },
    { value: "2", label: "Failed" },
    { value: "3", label: "Attached" },
    { value: "4", label: "Linked Expense" },
    { value: "5", label: "Already Attached" },
    { value: "6", label: "Future Receipt" },
    { value: "7", label: "CC Line Item" },
    { value: "9", label: "All" },
];

// Helper function to get status label
const getStatusLabel = (statusValue: string): string => {
    const found = statusOptions.find(option => option.value === statusValue);
    return found?.label || statusValue;
};

const InvoiceDraftFilterForm: React.FC<InvoiceDraftFilterFormProps> = ({
    onApplyFilters,
    isApplyingFilters = false,
    initialFilters = {},
}) => {
    const currentDate = moment().format('MM/DD/YYYY');
    const threeDaysAgo = moment().subtract(7, 'days').format('MM/DD/YYYY');

    // Fetch users for dropdown
    const { data: getUserByManager } = useGetAllUsersManagerAdminQuery('');
    // Fetch record sources for dropdown
    const { data: recordSourceLookup } = useGetAllRecordSourceLookupQuery('');

    // Local filter states
    const [localFilterByStatus, setLocalFilterByStatus] = useState(initialFilters?.filterByStatus || "0");
    const [localFilterByStartDate, setLocalFilterByStartDate] = useState(initialFilters?.filterByStartDate || threeDaysAgo);
    const [localFilterByEndDate, setLocalFilterByEndDate] = useState(initialFilters?.filterByEndDate || currentDate);
    const [localFilterByUserIds, setLocalFilterByUserIds] = useState(initialFilters?.filterByUserIds || "");
    const [localSearchTerm, setLocalSearchTerm] = useState(initialFilters?.searchTerm || "");
    const [localFilterByItemStartDate, setLocalFilterByItemStartDate] = useState(initialFilters?.filterByItemStartDate || "");
    const [localFilterByItemEndDate, setLocalFilterByItemEndDate] = useState(initialFilters?.filterByItemEndDate || "");
    const [localFilterByItemAmount, setLocalFilterByItemAmount] = useState(initialFilters?.filterByItemAmount || "");
    const [localFilterByReceiptStartDate, setLocalFilterByReceiptStartDate] = useState(initialFilters?.filterByReceiptStartDate || threeDaysAgo);
    const [localFilterByReceiptEndDate, setLocalFilterByReceiptEndDate] = useState(initialFilters?.filterByReceiptEndDate || currentDate);
    const [localFilterByReceiptInvoiceNo, setLocalFilterByReceiptInvoiceNo] = useState(initialFilters?.filterByReceiptInvoiceNo || "");
    const [localFilterByReceiptAmount, setLocalFilterByReceiptAmount] = useState(initialFilters?.filterByReceiptAmount || "");
    const [localFilterByItemSource, setLocalFilterByItemSource] = useState(initialFilters?.filterByItemSource || 0);
    const [localFilterByReceiptSource, setLocalFilterByReceiptSource] = useState(initialFilters?.filterByReceiptSource || 0);
    const [localInvoiceDataDraftId, setLocalInvoiceDataDraftId] = useState(initialFilters?.invoiceDataDraftId || "");

    // Handlers
    const handleStatusChange = (selectedOption: any) => {
        setLocalFilterByStatus(selectedOption.value);
    };

    const handleFilterByUser = (selectedOption: any) => {
        setLocalFilterByUserIds(selectedOption?.value || "");
    };

    const handleSearchChange = (e: any) => {
        setLocalSearchTerm(e.target.value);
    };

    const handleItemAmountChange = (e: any) => {
        setLocalFilterByItemAmount(e.target.value);
    };

    const handleReceiptInvoiceNoChange = (e: any) => {
        setLocalFilterByReceiptInvoiceNo(e.target.value);
    };

    const handleReceiptAmountChange = (e: any) => {
        setLocalFilterByReceiptAmount(e.target.value);
    };

    const handleInvoiceDataDraftIdChange = (e: any) => {
        setLocalInvoiceDataDraftId(e.target.value);
    };

    const handleItemSourceChange = (selectedOption: any) => {
        setLocalFilterByItemSource(selectedOption?.value || 0);
    };

    const handleReceiptSourceChange = (selectedOption: any) => {
        setLocalFilterByReceiptSource(selectedOption?.value || 0);
    };

    const handleDateRangeChange = (event: any, picker: any) => {
        const startDateFormatted = moment(picker.startDate).format('MM-DD-YYYY');
        const endDateFormatted = moment(picker.endDate).format('MM-DD-YYYY');
        setLocalFilterByStartDate(startDateFormatted);
        setLocalFilterByEndDate(endDateFormatted);
    };

    const handleCancelDateSelection = (event: any, picker: any) => {
        setLocalFilterByStartDate("");
        setLocalFilterByEndDate("");
    };

    const handleItemDateRangeChange = (event: any, picker: any) => {
        const startDateFormatted = moment(picker.startDate).format('MM-DD-YYYY');
        const endDateFormatted = moment(picker.endDate).format('MM-DD-YYYY');
        setLocalFilterByItemStartDate(startDateFormatted);
        setLocalFilterByItemEndDate(endDateFormatted);
    };

    const handleItemCancelDateSelection = (event: any, picker: any) => {
        setLocalFilterByItemStartDate("");
        setLocalFilterByItemEndDate("");
    };

    const handleReceiptDateRangeChange = (event: any, picker: any) => {
        const startDateFormatted = moment(picker.startDate).format('MM-DD-YYYY');
        const endDateFormatted = moment(picker.endDate).format('MM-DD-YYYY');
        setLocalFilterByReceiptStartDate(startDateFormatted);
        setLocalFilterByReceiptEndDate(endDateFormatted);
    };

    const handleReceiptCancelDateSelection = (event: any, picker: any) => {
        setLocalFilterByReceiptStartDate("");
        setLocalFilterByReceiptEndDate("");
    };

    const applyFilters = () => {
        const filters = {
            filterByStatus: localFilterByStatus || "0",
            filterByStartDate: localFilterByStartDate || "",
            filterByEndDate: localFilterByEndDate || "",
            filterByUserIds: localFilterByUserIds || "",
            searchTerm: localSearchTerm || "",
            filterByItemStartDate: localFilterByItemStartDate || "",
            filterByItemEndDate: localFilterByItemEndDate || "",
            filterByItemAmount: localFilterByItemAmount || "",
            filterByReceiptStartDate: localFilterByReceiptStartDate || "",
            filterByReceiptEndDate: localFilterByReceiptEndDate || "",
            filterByReceiptInvoiceNo: localFilterByReceiptInvoiceNo || "",
            filterByReceiptAmount: localFilterByReceiptAmount || "",
            filterByItemSource: localFilterByItemSource || 0,
            filterByReceiptSource: localFilterByReceiptSource || 0,
            invoiceDataDraftId: localInvoiceDataDraftId || "",
        };
        onApplyFilters(filters);
    };

    const resetFilters = () => {
        setLocalFilterByStartDate(threeDaysAgo);
        setLocalFilterByEndDate(currentDate);
        setLocalFilterByUserIds("");
        setLocalFilterByStatus("0");
        setLocalSearchTerm("");
        setLocalFilterByItemStartDate("");
        setLocalFilterByItemEndDate("");
        setLocalFilterByItemAmount("");
        setLocalFilterByReceiptStartDate(threeDaysAgo);
        setLocalFilterByReceiptEndDate(currentDate);
        setLocalFilterByReceiptInvoiceNo("");
        setLocalFilterByReceiptAmount("");
        setLocalFilterByItemSource(0);
        setLocalFilterByReceiptSource(0);
        setLocalInvoiceDataDraftId("");

        // Apply reset filters
        const filters = {
            filterByStatus: "0",
            filterByStartDate: threeDaysAgo,
            filterByEndDate: currentDate,
            filterByUserIds: "",
            searchTerm: "",
            filterByItemStartDate: "",
            filterByItemEndDate: "",
            filterByItemAmount: "",
            filterByReceiptStartDate: "",
            filterByReceiptEndDate: "",
            filterByReceiptInvoiceNo: "",
            filterByReceiptAmount: "",
            filterByItemSource: 0,
            filterByReceiptSource: 0,
            invoiceDataDraftId: "",
        };
        onApplyFilters(filters);
    };

    return (
        <div className="row pt-3">
            {/* Row 1 - First 4 filters */}
            <div className='col-md-4 col-lg-2 col-xl-2 mb-5'>
                <TextField
                    label='Expense Tracking ID:'
                    placeholder='Search by Expense Tracking ID'
                    value={localSearchTerm}
                    onChange={handleSearchChange}
                />
            </div>
            <div className="col-md-4 col-lg-2 col-xl-2 mb-5">
                <SearchSelect
                    label='User:'
                    options={[
                        ...(getUserByManager?.result || []).map((option: any) => ({
                            value: option.id,
                            label: option?.value,
                        })),
                    ]}
                    placeholder='Select User'
                    isClearable={true}
                    value={localFilterByUserIds && localFilterByUserIds !== ''
                        ? {
                            value: localFilterByUserIds,
                            label: getUserByManager?.result?.find((option: any) => option.id === localFilterByUserIds)?.value || localFilterByUserIds
                        }
                        : null
                    }
                    onChange={handleFilterByUser}
                />
            </div>
            <div className='col-md-4 col-lg-2 col-xl-2 mb-5'>
                <SearchSelect
                    label='Receipt Status:'
                    options={statusOptions}
                    placeholder='Select Status'
                    value={
                        localFilterByStatus && localFilterByStatus !== '0'
                            ? {
                                value: localFilterByStatus,
                                label: getStatusLabel(localFilterByStatus)
                            }
                            : { value: "0", label: "All Statuses" }
                    }
                    onChange={handleStatusChange}
                />
            </div>
            <div className='col-md-4 col-lg-2 col-xl-2 mb-5'>
                <DateRangeComp
                    label={'Receipts Uploaded Date'}
                    startDate={localFilterByStartDate || ''}
                    endDate={localFilterByEndDate || ''}
                    onApply={handleDateRangeChange}
                    onCancel={handleCancelDateSelection}
                    value={localFilterByStartDate && localFilterByEndDate ? `${localFilterByStartDate} - ${localFilterByEndDate}` : "MM/DD/YYYY"}
                />
            </div>
            <div className='col-md-4 col-lg-2 col-xl-2 mb-5'>
                <TextField
                    label='Search by Receipt No:'
                    placeholder='Enter Receipt Number'
                    value={localFilterByReceiptInvoiceNo}
                    onChange={handleReceiptInvoiceNoChange}
                />
            </div>
            {/* Row 2 - Next 4 filters */}
            <div className='col-md-4 col-lg-2 col-xl-2 mb-5'>
                <DateRangeComp
                    label={'Item Date'}
                    startDate={localFilterByItemStartDate || ''}
                    endDate={localFilterByItemEndDate || ''}
                    onApply={handleItemDateRangeChange}
                    onCancel={handleItemCancelDateSelection}
                    value={localFilterByItemStartDate && localFilterByItemEndDate ? `${localFilterByItemStartDate} - ${localFilterByItemEndDate}` : "MM/DD/YYYY"}
                />
            </div>
            <div className='col-md-4 col-lg-2 col-xl-2 mb-5'>
                <DateRangeComp
                    label={'Receipt Date'}
                    startDate={localFilterByReceiptStartDate || ''}
                    endDate={localFilterByReceiptEndDate || ''}
                    onApply={handleReceiptDateRangeChange}
                    onCancel={handleReceiptCancelDateSelection}
                    value={localFilterByReceiptStartDate && localFilterByReceiptEndDate ? `${localFilterByReceiptStartDate} - ${localFilterByReceiptEndDate}` : "MM/DD/YYYY"}
                />
            </div>
            <div className='col-md-4 col-lg-2 col-xl-2 mb-5'>
                <TextField
                    label='Item Amount:'
                    placeholder='Enter Item Amount'
                    type="number"
                    value={localFilterByItemAmount}
                    onChange={handleItemAmountChange}
                />
            </div>
            {/* Row 3 - Remaining filters + action buttons */}
            <div className='col-md-4 col-lg-2 col-xl-2 mb-5'>
                <TextField
                    label='Receipt Amount:'
                    placeholder='Enter Receipt Amount'
                    type="number"
                    value={localFilterByReceiptAmount}
                    onChange={handleReceiptAmountChange}
                />
            </div>
            <div className='col-md-4 col-lg-2 col-xl-2 mb-5'>
                <SearchSelect
                    label='Item Source:'
                    options={[
                        ...(Array.isArray(recordSourceLookup?.result) ? recordSourceLookup.result.map((option: any) => ({
                            value: option.id,
                            label: option?.value,
                        })) : [])
                    ]}
                    placeholder='Select Item Source'
                    isClearable={true}
                    value={localFilterByItemSource && localFilterByItemSource !== 0
                        ? {
                            value: localFilterByItemSource,
                            label: recordSourceLookup?.result?.find((option: any) => option.id === localFilterByItemSource)?.value
                        }
                        : null
                    }
                    onChange={handleItemSourceChange}
                />
            </div>
            <div className='col-md-4 col-lg-2 col-xl-2 mb-5'>
                <SearchSelect
                    label='Receipt Uploaded From:'
                    options={
                        Array.isArray(recordSourceLookup?.result)
                            ? recordSourceLookup.result
                                .filter((option: any) => {
                                    const value = String(option?.value || '').toLowerCase();
                                    return value === 'webapp' || value === 'mobile';
                                })
                                .map((option: any) => ({
                                    value: option.id,
                                    label: option?.value,
                                }))
                            : []
                    }
                    placeholder='Select Receipt Source'
                    isClearable={true}
                    value={localFilterByReceiptSource && localFilterByReceiptSource !== 0
                        ? {
                            value: localFilterByReceiptSource,
                            label: recordSourceLookup?.result?.find((option: any) => option.id === localFilterByReceiptSource)?.value
                        }
                        : null
                    }
                    onChange={handleReceiptSourceChange}
                />
            </div>
            <div className='col-md-4 col-lg-2 col-xl-2 mb-5'>
                <TextField
                    label='SS Receipt ID'
                    placeholder='Enter SS Receipt ID'
                    value={localInvoiceDataDraftId}
                    onChange={handleInvoiceDataDraftIdChange}
                />
            </div>
            <div className='col-md-4 col-lg-2 col-xl-2 mb-5'>
                <div className='d-flex align-items-end h-100 gap-3 pb-2'>
                    <button className='btn btn-sm btn-danger' onClick={resetFilters}>Reset Filters</button>
                    <button className='btn btn-sm btn-primary' onClick={applyFilters}>
                        {isApplyingFilters ? <>
                            <span>loading...</span>
                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                        </> : "Apply Filters"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default InvoiceDraftFilterForm;